import './AgentSessionManager.scss';
import cn from 'classnames';
import {
  ReactNode,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import { MdOutlineAddComment, MdOutlineHistory } from 'react-icons/md';
import { v4 as uuidv4 } from 'uuid';
import { Button } from '@Components/Button';
import { Appearance, Design, Size } from '@Components/designConstants';
import {
  GetConversationResponse,
  GetSessionsResponse,
  LLMSession,
  SaveLLMMessageResponse,
  SessionId,
  UpdateSessionArgs
} from '@blisspointmedia/bpm-types/dist/LLM';
import { AgentHandoff, AgentToolSpec } from '@blisspointmedia/bpm-types/dist/agents';
import { useAppSelector } from '../../redux';
import { emailSelector } from '../../redux/user';
import { useSetError } from '../../redux/modals';
import { reportError } from '../../utils/errorReporting';
import {
  AgentAttachment,
  AgentCustomEvent,
  AgentResponse,
  AgentResponseSegment,
  sendPromptToAgent
} from '../../utils/agent-client';
import { NotSignedInError } from '../../utils/auth/auth-errors';
import { awaitJSON, MiscLambdaFetch } from '../../utils/fetch-utils';
import { useLocalStorageState } from '../../utils/hooks/useData';
import { AgentType, supportsCancel } from '../../utils/agent-endpoints';
import { AgentChat } from './AgentChat';
import {
  AgentMessage,
  AssistantChatMessage,
  FeedbackSubmission,
  MessageRole,
  PendingAttachment
} from './agent-chat-types';
import { getTurnOutcome, hydrateStoredMessages, withAttachmentMarker } from './agent-chat-utils';
import { SessionHistoryPanel } from './SessionHistoryPanel';

// Imperative escape hatch for a host that needs to drive the session from OUTSIDE the normal
// typed-in PromptInput flow (e.g. AgentDrawer starting a fresh chat for a just-instantiated template
// and offering its starter prompt for the user to edit or send). Exposed via `ref` rather than a
// prop so it stays a deliberate, rare exception, not something every host has to think about.
export interface AgentSessionManagerHandle {
  startNewSession: () => void;
  prefillPrompt: (prompt: string) => void;
}

// One run's target, for a host that routes turns between agents (the combined agent chat).
export interface AgentRoute {
  agent: AgentType;
  endpoint: string;
  tools?: AgentToolSpec[];
}

// What to do about a `handoff` tool call in a response: always a note on that message, plus the run
// to make next when the handoff goes ahead.
export interface HandoffDecision {
  note: string;
  followUp?: AgentRoute & { handoff: AgentHandoff };
}

interface RunTurnArgs {
  sessionId: string;
  prompt: string;
  attachment?: AgentAttachment;
  route?: AgentRoute;
  handoff?: AgentHandoff;
  isFirstInSession: boolean;
  isFollowUp: boolean;
  // Aborted when the user clicks Stop.
  signal?: AbortSignal;
}

interface AgentSessionManagerProps {
  // The AgentCore runtime the AG-UI transport talks to. Not needed when every turn is given a `route`.
  agentEndpoint?: string;
  company?: string;
  emptyState?: ReactNode;
  suggestedPrompts?: string[];
  // Defaults to false (feedback on) — every host persists feedback via /submitLLMFeedback (see
  // onFeedback below), so this only needs to be set to true to opt a host out.
  disableFeedback?: boolean;
  // Invoked in addition to the built-in /submitLLMFeedback persistence, for hosts that want
  // an extra side effect (e.g. analytics). Not required to persist feedback.
  onFeedback?: (submission: FeedbackSubmission) => void;
  // When false, the prompt textarea will not auto-focus — pass the host panel's
  // visibility flag when rendering inside a FloatingPanel that starts closed.
  visible?: boolean;
  placeholder?: string;
  promptTip?: string;
  // Called when the prompt tip is actually drawn (empty chat), so a host can track what's been seen.
  onPromptTipShown?: (tip: string) => void;
  // Called whenever a fresh chat starts, from the New chat button or the imperative handle.
  onNewSession?: () => void;
  className?: string;
  // Invoked once when a run completes, with the finalized response (text + tool
  // calls + artifacts, post artifact-extraction). Optional — existing hosts
  // (InsightsAgent, StreamingFlightPlan) pass nothing.
  // `agent` is the runtime that answered, set only for hosts that pass `routeTurn`.
  onAgentResponse?: (response: AgentResponse, agent?: AgentType) => void;
  // Optional grounding context sent as a leading `system` message on the FIRST prompt of
  // each session only (AgentCore session memory carries it forward). The dashboard agent
  // uses it to inject the ChartConfig shape + valid-id catalog. Other hosts pass nothing.
  sessionContext?: string;
  // Optional per-turn context sent on EVERY prompt (it can change between turns, unlike
  // sessionContext). The dashboard agent uses it to carry the artifact currently in focus so a
  // refinement edits that chart/dashboard in place. Other hosts pass nothing.
  focusContext?: string;
  // Which agent owns this session (e.g. Agent.DASHBOARD_CHARTS). Persisted on saveLLMMessage so the shared
  // llm_sessions table can be attributed per agent. Hosts that don't set it leave the row's agent null.
  agent?: AgentType;
  // Model-run selection forwarded to the agent (via the AG-UI run's forwardedProps) so it loads the
  // matching run. Only MMM hosts set them; other hosts leave the agent on its default-resolution path.
  kpi?: string;
  timestamp?: string;
  // Which MMM product the host is ('dtc' / 'commerce'), forwarded so the agent names it correctly.
  modelType?: string;
  // Fired for each AG-UI CUSTOM event the agent emits mid-run (forwarded by name/value). Generic —
  // the host interprets the names it cares about (the Elevate demo reads governance.* for its audit
  // trail). Other hosts pass nothing.
  onCustomEvent?: (event: AgentCustomEvent) => void;
  // When true, this host begins a brand-new session on every mount instead of resuming the
  // persisted one, and never writes the shared localStorage id. History still resumes any past
  // session. Opt-in: hosts that want a long-lived thread across reloads leave it false.
  startFresh?: boolean;
  // Routes each user turn to an agent, given the session's active agent (whoever answered last, else
  // `agent`). `agent` itself stays the HOST: localStorage key, History filter and session attribution.
  // Only the combined agent chat sets these three; everywhere else every run goes to `agentEndpoint`.
  routeTurn?: (activeAgent: string | undefined) => AgentRoute;
  // Reads a finished run for a handoff. A follow-up runs in the same turn with the user's prompt.
  resolveHandoff?: (
    response: AgentResponse,
    routedAgent: AgentType,
    isFollowUp: boolean,
    // The handoff this run was started with, if any.
    sentHandoff?: AgentHandoff,
    // This session's messages so far, for a host that needs earlier turns.
    threadMessages?: AgentMessage[]
  ) => HandoffDecision | null;
  // A small label over an assistant message, from the agent that produced it.
  messageLabel?: (agent: string | undefined) => string | undefined;
  // Lets the user attach one CSV to a message (see AgentChat). Only for agents that accept it.
  allowCsvAttachment?: boolean;
  // React 19 accepts `ref` as an ordinary prop on a function component — no forwardRef needed.
  // See AgentSessionManagerHandle above for why this exists.
  ref?: Ref<AgentSessionManagerHandle>;
}

export const AgentSessionManager = ({
  agentEndpoint,
  company,
  emptyState,
  suggestedPrompts,
  disableFeedback = false,
  onFeedback,
  visible = true,
  placeholder,
  promptTip,
  onPromptTipShown,
  onNewSession,
  className,
  onAgentResponse,
  sessionContext,
  focusContext,
  agent,
  kpi,
  timestamp,
  modelType,
  onCustomEvent,
  startFresh = false,
  routeTurn,
  resolveHandoff,
  messageLabel,
  allowCsvAttachment = false,
  ref
}: AgentSessionManagerProps) => {
  const setError = useSetError();
  const userEmail = useAppSelector(emailSelector);

  // Session ids that have already received the grounding context, so we send it once per
  // session (on the first prompt) rather than on every message.
  const groundedSessions = useRef(new Set<string>());

  // Each session is identified by a UUID that is also threaded to AgentCore as
  // the session/thread ID, giving the AI per-session memory continuity. By default the id
  // is persisted in localStorage — scoped per agent + company so no two agents
  // ever share a thread — so a page reload resumes the same conversation (the
  // agent's server-side memory keyed on this id carries prior turns forward)
  // instead of starting a fresh, empty session. `startFresh` hosts opt out (below).
  const sessionStorageKey = `agent-session:${agent ?? 'default'}:${company ?? 'default'}`;
  const [storedSessionId, setStoredSessionId] = useLocalStorageState<string>(
    sessionStorageKey,
    prev => prev ?? uuidv4()
  );

  // A startFresh host keeps its id in component state instead, so the thread lives exactly as
  // long as this mount and the persisted id is left untouched for hosts that do resume.
  const [ephemeralSessionId, setEphemeralSessionId] = useState<string | undefined>(() => uuidv4());

  const currentSessionId = (startFresh ? ephemeralSessionId : storedSessionId) ?? '';
  const setCurrentSessionId = startFresh ? setEphemeralSessionId : setStoredSessionId;

  // useLocalStorageState only writes on set, not on lazy init, so persist the
  // initial id once on mount — otherwise a reload during the first (never-reset)
  // session would mint a new id and lose continuity.
  useEffect(() => {
    if (startFresh) return;
    setStoredSessionId(prev => prev ?? uuidv4());
  }, [setStoredSessionId, startFresh]);
  // Messages are stored per-session so switching sessions preserves history.
  const [messagesMap, setMessagesMap] = useState<Record<string, AgentMessage[]>>({});
  const [draftMessage, setDraftMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  // Whether the run in flight can be cancelled (its agent is in AGENTS_SUPPORTING_CANCEL); decides
  // whether the chat offers Stop. `stopping` covers the gap between Stop and the stream ending.
  const [cancellable, setCancellable] = useState(false);
  const [stopping, setStopping] = useState(false);

  // Sessions whose persisted transcript we've already pulled (or that were created
  // fresh this mount and so have none). Guards against re-fetching and against
  // clobbering messages that were streamed in live. A startFresh host's initial id is
  // seeded here so it skips a hydration fetch that could only come back empty.
  const hydratedSessions = useRef(
    new Set<string>(startFresh && ephemeralSessionId ? [ephemeralSessionId] : [])
  );

  // Latest messages for runTurn, which is memoized and would otherwise read a stale map.
  const messagesMapRef = useRef(messagesMap);
  useEffect(() => {
    messagesMapRef.current = messagesMap;
  }, [messagesMap]);

  const messages = messagesMap[currentSessionId] ?? [];
  const hasUserMessages = messages.some(m => m.role === MessageRole.USER);

  // The agent answering this session now: whoever produced the last assistant message, else the host.
  // Derived rather than stored, so New Chat and History resume need no special cases.
  const activeAgent =
    [...messages].reverse().find((m): m is AssistantChatMessage => m.role === MessageRole.ASSISTANT)
      ?.agent ?? agent;

  // A handoff follow-up is skipped if the user has moved to another session mid-run, or the chat
  // has unmounted (left the route).
  const currentSessionIdRef = useRef(currentSessionId);
  useEffect(() => {
    currentSessionIdRef.current = currentSessionId;
  }, [currentSessionId]);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Persisting the session id alone leaves the transcript empty on reload — the
  // agent still remembers the thread server-side, but the UI shows nothing. Pull
  // the stored messages for the resumed session so its prior turns re-render.
  useEffect(() => {
    const sessionId = currentSessionId;
    if (!sessionId || hydratedSessions.current.has(sessionId)) return undefined;
    hydratedSessions.current.add(sessionId);

    let cancelled = false;
    (async () => {
      try {
        const res = await MiscLambdaFetch(
          `/getLLMConversation?sessionId=${encodeURIComponent(sessionId)}`
        );
        const { messages: stored } = await awaitJSON<GetConversationResponse>(res);
        if (cancelled || stored.length === 0) return;

        // Don't overwrite anything already streamed into this session live.
        setMessagesMap(prev =>
          prev[sessionId]?.length ? prev : { ...prev, [sessionId]: hydrateStoredMessages(stored) }
        );
        // The grounding context was already sent on this session's original first
        // prompt, so the next prompt must not be treated as first-in-session.
        groundedSessions.current.add(sessionId);
      } catch (e) {
        if (cancelled) return;
        setError({
          title: "Couldn't load conversation history",
          message: e.message,
          reportError: e
        });
        // Allow a later render to retry rather than leaving the thread empty.
        hydratedSessions.current.delete(sessionId);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentSessionId, setError]);

  const startNewSession = useCallback(() => {
    const newSessionId = uuidv4();
    // A fresh session has no persisted history — skip the hydration fetch for it.
    hydratedSessions.current.add(newSessionId);
    setCurrentSessionId(newSessionId);
    setDraftMessage('');
    onNewSession?.();
  }, [setCurrentSessionId, onNewSession]);

  // Past sessions for the History panel. undefined = not fetched yet, fetched lazily
  // when the panel opens so this doesn't affect the existing localStorage-continuity
  // startup behavior.
  const [sessions, setSessions] = useState<LLMSession[] | undefined>(undefined);
  const [showHistory, setShowHistory] = useState(false);

  const fetchSessions = useCallback(async () => {
    if (!company || !userEmail) {
      // No terminal state to fetch into — resolve to empty rather than leaving the
      // panel showing its loading spinner forever.
      setSessions([]);
      return;
    }
    try {
      const res = await MiscLambdaFetch(
        `/getLLMSessions?user=${encodeURIComponent(userEmail)}&client=${encodeURIComponent(company)}`
      );
      const { sessions: fetched } = await awaitJSON<GetSessionsResponse>(res);
      // The backend scopes getSessions by user+client only, not agent — filter client-side
      // so a page for one agent doesn't show sessions started by other agents.
      setSessions(fetched.filter(session => session.agent === agent));
    } catch (e) {
      console.error('Failed to load sessions:', e);
      setSessions([]);
    }
  }, [agent, company, userEmail]);

  // Re-fetch on every open (not just the first) so a session created earlier this
  // mount — e.g. the one just started via sendPrompt/startNewSession — shows up.
  // Sessions already loaded stay visible while this refetch is in flight.
  useEffect(() => {
    if (showHistory) {
      fetchSessions();
    }
  }, [showHistory, fetchSessions]);

  const updateSession = useCallback(async (updateArgs: UpdateSessionArgs) => {
    await MiscLambdaFetch('/updateLLMSession', {
      method: 'POST',
      body: JSON.stringify(updateArgs)
    });
    setSessions(prev =>
      prev?.map(session =>
        session.sessionId === updateArgs.sessionId
          ? {
              ...session,
              ...(updateArgs.newState !== undefined && { state: updateArgs.newState }),
              ...(updateArgs.newName !== undefined && { sessionName: updateArgs.newName })
            }
          : session
      )
    );
  }, []);

  const selectSession = useCallback(
    (sessionId: SessionId) => {
      setCurrentSessionId(sessionId);
      setShowHistory(false);
    },
    [setCurrentSessionId]
  );

  const submitFeedback = useCallback(
    (submission: FeedbackSubmission) => {
      const message = messagesMap[currentSessionId]?.find(m => m.id === submission.messageId);
      const persistedId = message?.role === MessageRole.ASSISTANT ? message.persistedId : undefined;
      // Toggling feedback off is a local-only affordance — the backend has no "clear
      // feedback" call. Also skip if the assistant message hasn't finished persisting yet.
      if (persistedId && submission.feedback) {
        MiscLambdaFetch('/submitLLMFeedback', {
          method: 'POST',
          body: JSON.stringify({
            messageId: persistedId,
            type: submission.feedback,
            reason: submission.reason ?? undefined
          })
        }).catch(e =>
          setError({ title: "Couldn't submit feedback", message: e.message, reportError: e })
        );
      }
      onFeedback?.(submission);
    },
    [currentSessionId, messagesMap, onFeedback, setError]
  );

  // One agent run within a user turn: its own assistant bubble, the stream, then the persist. Resolves
  // with the handoff decision for the response (null when there is none, or the run failed).
  const runTurn = useCallback(
    async ({
      sessionId,
      prompt,
      attachment,
      route,
      handoff,
      isFirstInSession,
      isFollowUp,
      signal
    }: RunTurnArgs): Promise<HandoffDecision | null> => {
      const assistantId = uuidv4();
      const routedAgent = route?.agent;
      const runningAgent = routedAgent ?? agent;
      setCancellable(runningAgent !== undefined && supportsCancel(runningAgent));
      setMessagesMap(prev => ({
        ...prev,
        [sessionId]: [
          ...(prev[sessionId] ?? []),
          {
            id: assistantId,
            role: MessageRole.ASSISTANT,
            segments: [],
            timestamp: Date.now(),
            ...(routedAgent && { agent: routedAgent })
          }
        ]
      }));
      const dropEmptyAssistant = () =>
        setMessagesMap(prev => ({
          ...prev,
          [sessionId]: (prev[sessionId] ?? []).filter(
            m => m.id !== assistantId || (m.role === MessageRole.ASSISTANT && m.segments.length > 0)
          )
        }));
      const setSegments = (segments: AgentResponseSegment[]) => {
        setMessagesMap(prev => ({
          ...prev,
          [sessionId]: (prev[sessionId] ?? []).map(m =>
            m.id === assistantId && m.role === MessageRole.ASSISTANT ? { ...m, segments } : m
          )
        }));
      };
      // Grounding and focus context are the host agent's. A run routed to another agent gets only
      // its typed handoff.
      const isHostRun = !route || route.agent === agent;

      try {
        if (!agentEndpoint && !route) {
          throw new Error('AgentSessionManager needs either agentEndpoint or route');
        }
        const response = await sendPromptToAgent({
          attachment,
          agentEndpoint: route?.endpoint ?? agentEndpoint ?? '',
          company,
          kpi,
          timestamp,
          modelType,
          sessionId,
          prompt,
          sessionContext: isHostRun && isFirstInSession ? sessionContext : undefined,
          focusContext: isHostRun ? focusContext : undefined,
          onCustomEvent,
          onUpdate: setSegments,
          signal,
          tools: route?.tools,
          handoff
        });

        const outcome = getTurnOutcome(response);
        if (outcome === 'discarded') {
          dropEmptyAssistant();
          return null;
        }

        // Fire once with the finalized response — text + tool calls + artifacts
        // after artifact-extraction — rather than partial streaming ticks. A stopped run is
        // persisted with its partial segments (the backend ends them with "Cancelled by user.")
        // but is no finished answer: no host callback, no handoff.
        const isStopped = outcome === 'stopped';
        if (!isStopped) {
          onAgentResponse?.(response, routedAgent);
        }

        const decision =
          !isStopped && resolveHandoff && routedAgent
            ? resolveHandoff(
                response,
                routedAgent,
                isFollowUp,
                handoff,
                messagesMapRef.current[sessionId]
              )
            : null;
        const segments: AgentResponseSegment[] = decision
          ? [...response.segments, { type: 'note', content: decision.note }]
          : response.segments;
        if (decision) {
          setSegments(segments);
        }

        // Persist the assistant message after streaming completes.
        // Segments are stored as JSON so history loading can reconstruct rich content.
        // Note: artifact segments contain pre-signed S3 URLs which expire; they will
        // appear as dead download links when loaded from history after expiry.
        if (company && userEmail) {
          const segmentsForStorage = segments.map(seg =>
            seg.type === 'artifact' ? { type: seg.type, filename: seg.filename } : seg
          );
          // Fire-and-forget — don't block clearing `submitting` on this persist call.
          (async () => {
            try {
              const res = await MiscLambdaFetch('/saveLLMMessage', {
                method: 'POST',
                body: JSON.stringify({
                  sessionId,
                  client: company,
                  userEmail,
                  messageType: 'RESPONSE',
                  contents: JSON.stringify(segmentsForStorage),
                  agent,
                  // The runtime that produced it; differs from the host `agent` after a handoff.
                  ...(routedAgent && { messageAgent: routedAgent })
                })
              });
              // Feedback needs the real llm_messages.id, not the client-generated assistantId.
              const { messageId } = await awaitJSON<SaveLLMMessageResponse>(res);
              setMessagesMap(prev => ({
                ...prev,
                [sessionId]: (prev[sessionId] ?? []).map(m =>
                  m.id === assistantId ? { ...m, persistedId: messageId } : m
                )
              }));
            } catch (e) {
              reportError(e, { operation: 'agentChat.saveMessage' });
            }
          })();
        }
        return decision;
      } catch (e) {
        // Drop the empty assistant placeholder so the chat doesn't show a
        // blank bubble if the run errored before producing any content.
        dropEmptyAssistant();
        // An ended session swaps the app to the login view; an error modal over it would be noise.
        if (!(e instanceof NotSignedInError)) {
          setError({ message: e.message, reportError: e });
        }
        return null;
      }
    },
    [
      agent,
      agentEndpoint,
      company,
      kpi,
      timestamp,
      modelType,
      onAgentResponse,
      resolveHandoff,
      sessionContext,
      focusContext,
      onCustomEvent,
      setError,
      userEmail
    ]
  );

  const sendPrompt = useCallback(
    async (prompt: string, attachment?: PendingAttachment) => {
      if (submitting) return;

      // Capture sessionId at call time; functional updates below read the
      // latest state so the correct session's messages are always updated.
      const sessionId = currentSessionId;

      // Ground the agent once per session, on the first prompt only.
      const isFirstInSession = !groundedSessions.current.has(sessionId);
      if (isFirstInSession) {
        groundedSessions.current.add(sessionId);
      }

      const chatAttachment = attachment && { filename: attachment.filename, size: attachment.size };
      setMessagesMap(prev => ({
        ...prev,
        [sessionId]: [
          ...(prev[sessionId] ?? []),
          {
            id: uuidv4(),
            role: MessageRole.USER,
            content: prompt,
            timestamp: Date.now(),
            ...(chatAttachment && { attachment: chatAttachment })
          }
        ]
      }));
      // Held across the run AND any handoff follow-up, so the user can't send in between.
      setSubmitting(true);
      const controller = new AbortController();
      const { signal } = controller;
      abortControllerRef.current = controller;

      // Persist the user message immediately. Fire-and-forget — don't block the stream.
      // sessionName seeds the session record on first insert; subsequent calls hit ON CONFLICT DO NOTHING.
      // An attachment is saved as a filename marker only; its bytes go to the agent and nowhere else.
      if (company && userEmail) {
        MiscLambdaFetch('/saveLLMMessage', {
          method: 'POST',
          body: JSON.stringify({
            sessionId,
            client: company,
            userEmail,
            messageType: 'PROMPT',
            contents: withAttachmentMarker(prompt, chatAttachment),
            sessionName: (prompt || attachment?.filename || '').substring(0, 256),
            agent
          })
        }).catch(e => reportError(e, { operation: 'agentChat.saveMessage' }));
      }

      try {
        const decision = await runTurn({
          sessionId,
          prompt,
          attachment,
          route: routeTurn?.(activeAgent),
          isFirstInSession,
          isFollowUp: false,
          signal
        });
        // The target gets the user's prompt verbatim (the adapter reads its prompt from the last user
        // message), with no new bubble and no PROMPT save: it's the same user turn.
        if (
          decision?.followUp &&
          !signal.aborted &&
          mountedRef.current &&
          currentSessionIdRef.current === sessionId
        ) {
          const { handoff, ...route } = decision.followUp;
          await runTurn({
            sessionId,
            prompt,
            route,
            handoff,
            isFirstInSession: false,
            isFollowUp: true,
            signal
          });
        }
      } finally {
        abortControllerRef.current = null;
        setStopping(false);
        setCancellable(false);
        setSubmitting(false);
      }
    },
    [agent, activeAgent, company, currentSessionId, routeTurn, runTurn, submitting, userEmail]
  );

  useImperativeHandle(ref, () => ({ startNewSession, prefillPrompt: setDraftMessage }), [
    startNewSession
  ]);

  // Cancel is cooperative: the stream may run on for a few seconds, so `submitting` stays true
  // until it actually resolves and the local request is never aborted.
  const stopGenerating = useCallback(() => {
    setStopping(true);
    abortControllerRef.current?.abort();
  }, []);

  return (
    <div className={cn('agent-session-manager', className)}>
      <div className='session-header'>
        {hasUserMessages && (
          <Button
            appearance={Appearance.EMPTY}
            design={Design.NEUTRAL}
            size={Size.SM}
            leadingElement={<MdOutlineAddComment />}
            onClick={startNewSession}
          >
            New Chat
          </Button>
        )}
        <Button
          appearance={Appearance.EMPTY}
          design={Design.NEUTRAL}
          size={Size.SM}
          className='history-toggle'
          leadingElement={<MdOutlineHistory />}
          onClick={() => setShowHistory(true)}
          aria-label='Chat history'
        >
          History
        </Button>
      </div>
      <AgentChat
        messages={messages}
        submitting={submitting}
        draftMessage={draftMessage}
        setDraftMessage={setDraftMessage}
        onSubmit={sendPrompt}
        onStop={cancellable ? stopGenerating : undefined}
        stopping={stopping}
        emptyState={emptyState}
        suggestedPrompts={suggestedPrompts}
        disableFeedback={disableFeedback}
        onFeedback={submitFeedback}
        visible={visible}
        placeholder={placeholder}
        promptTip={promptTip}
        onPromptTipShown={onPromptTipShown}
        messageLabel={messageLabel}
        allowCsvAttachment={allowCsvAttachment}
        // Clears a picked-but-unsent file when the chat changes. Only attachment hosts get it: it
        // remounts the prompt box, which takes focus, and a collapsed drawer mustn't be given focus.
        composerKey={allowCsvAttachment ? currentSessionId : undefined}
      />
      {showHistory && (
        <SessionHistoryPanel
          sessions={sessions}
          currentSessionId={currentSessionId}
          onSelectSession={selectSession}
          onStartNewSession={() => {
            startNewSession();
            setShowHistory(false);
          }}
          onUpdateSession={updateSession}
          onClose={() => setShowHistory(false)}
        />
      )}
    </div>
  );
};
